/**
 * WYSIWYG Handler Module
 * Handles all editor events (image upload, video upload, delete, paste, drop).
 */

import { WysiwygAPI } from "./wysiwyg-api";
import { WysiwygForm } from "./wysiwyg-form";
import { WysiwygUtils } from "./wysiwyg-utils";
import { notifier } from "../core/notifier/notifier";
import { translator } from "../translation/translator";
import { OgpPreviewHandler } from "../util/ogp/ogp-preview-handler";
import { Patterns } from "../constants/patterns";
import { UrlExtractor } from "../util/url/url-extractor";

interface QuillDeltaLike {
    ops?: Array<Record<string, unknown>>;
}

interface QuillRangeLike {
    index: number;
}

interface QuillBlotLike {
    length?: () => number;
}

interface QuillStaticLike {
    find?(node: Element): QuillBlotLike | null | undefined;
}

interface QuillClipboardLike {
    convert(html: string): QuillDeltaLike;
    dangerouslyPasteHTML(index: number, html: string, source?: string): void;
}

interface QuillRootLike extends HTMLElement {
    innerHTML: string;
}

interface QuillLike {
    root: QuillRootLike;
    clipboard: QuillClipboardLike;
    constructor?: QuillStaticLike;
    getSelection(focus?: boolean): QuillRangeLike | null;
    getLength(): number;
    getText(): string;
    setSelection(index: number, length: number, source?: string): void;
    setContents(delta: QuillDeltaLike, source?: string): void;
    insertEmbed(index: number, type: string, value: string, source?: string): void;
    deleteText(index: number, length: number, source?: string): void;
    update(source?: string): void;
    getIndex(blot: QuillBlotLike): number;
}

interface WysiwygRenderLike {
    trackContentImage(filename: string): void;
    removeContentImage(filename: string): void;
    updateContentImagesList(): void;
}

interface OgpPreviewRenderLike {
    detectUrls(content: string): string[];
}

interface OgpPreviewHandlerLike {
    ogpRender?: OgpPreviewRenderLike | null;
    updatePreviewWithUrls(urls: string[], container: HTMLElement): void;
    setPreviewSectionVisibility(container: HTMLElement, visible: boolean): void;
}

type TimeoutHandle = ReturnType<typeof setTimeout> | null;

type FileListHandler = (files: FileList) => Promise<void> | void;

import type * as z from "zod";
import { DeleteMedia200Response } from "../api/generated/posts.zod";

type DeleteMediaResult = z.infer<typeof DeleteMedia200Response>;

class WysiwygHandler {
    public quill: QuillLike;

    public render: WysiwygRenderLike;

    public textarea: HTMLTextAreaElement;

    public lastOgpUrls: string[];

    public isProcessingAutoLink: boolean;

    public autoLinkTimeout: TimeoutHandle;

    public isProcessingEmbedCleanup: boolean;

    public constructor(quill: QuillLike, render: WysiwygRenderLike, textarea: HTMLTextAreaElement) {
        this.quill = quill;
        this.render = render;
        this.textarea = textarea;
        this.lastOgpUrls = [];
        this.isProcessingAutoLink = false;
        this.autoLinkTimeout = null;
        this.isProcessingEmbedCleanup = false;
    }

    public static handlePasteEvent(
        event: ClipboardEvent,
        onImageFiles?: FileListHandler | null,
        onVideoFiles?: FileListHandler | null,
    ): void {
        if (!event.clipboardData || !event.clipboardData.files || !event.clipboardData.files.length) {
            return;
        }

        const files = event.clipboardData.files;
        const hasVideo = WysiwygUtils.hasVideoFiles(files);
        const hasImage = WysiwygUtils.hasImageFiles(files);

        if (hasVideo || hasImage) {
            event.preventDefault();

            if (hasVideo && onVideoFiles) {
                void onVideoFiles(files);
            }

            if (hasImage && onImageFiles) {
                void onImageFiles(files);
            }
        }
    }

    public static handleDropEvent(event: DragEvent, onVideoFiles?: FileListHandler | null): void {
        if (!event.dataTransfer || !event.dataTransfer.files || !event.dataTransfer.files.length) {
            return;
        }

        const hasVideo = WysiwygUtils.hasVideoFiles(event.dataTransfer.files);
        if (hasVideo && onVideoFiles) {
            event.preventDefault();
            event.stopPropagation();
            void onVideoFiles(event.dataTransfer.files);
        }
    }

    public getOgpPreviewContainer(): HTMLElement | null {
        const candidate = WysiwygForm.getOgpPreviewContainerForTextarea(this.textarea);

        return candidate instanceof HTMLElement ? candidate : null;
    }

    public getOgpPreviewTabContainer(): HTMLElement | null {
        const candidate = WysiwygForm.getOgpPreviewTabContainerForTextarea(this.textarea);

        return candidate instanceof HTMLElement ? candidate : null;
    }

    public detectOgpUrls(content: string): string[] {
        const ogpPreview = new OgpPreviewHandler() as OgpPreviewHandlerLike;
        const detected = ogpPreview.ogpRender ? ogpPreview.ogpRender.detectUrls(content) : [];
        return (detected || []).filter((url) => {
            const cleanedUrl = UrlExtractor.cleanUrl(url);
            return !Patterns.OGP_EXCLUDED_SNS_PLATFORMS.some((pattern) => pattern.test(cleanedUrl));
        });
    }

    public updateOgpPreviewNow(): void {
        try {
            const ogpPreviewContainer = this.getOgpPreviewContainer();
            const ogpPreviewTabContainer = this.getOgpPreviewTabContainer();
            if (!ogpPreviewContainer && !ogpPreviewTabContainer) return;

            const content = this.quill?.root?.innerHTML || "";
            const urls = this.detectOgpUrls(content);
            const ogpPreview = new OgpPreviewHandler() as OgpPreviewHandlerLike;

            this.lastOgpUrls = [...urls];

            if (urls.length === 0) {
                if (ogpPreviewContainer) {
                    ogpPreviewContainer.innerHTML = "";
                    ogpPreview.setPreviewSectionVisibility(ogpPreviewContainer, false);
                }

                if (ogpPreviewTabContainer) {
                    ogpPreviewTabContainer.innerHTML = "";
                    ogpPreview.setPreviewSectionVisibility(ogpPreviewTabContainer, false);
                }
                return;
            }

            if (ogpPreviewContainer) {
                void ogpPreview.updatePreviewWithUrls(urls, ogpPreviewContainer);
                ogpPreview.setPreviewSectionVisibility(ogpPreviewContainer, true);
            }

            if (ogpPreviewTabContainer) {
                void ogpPreview.updatePreviewWithUrls(urls, ogpPreviewTabContainer);
                ogpPreview.setPreviewSectionVisibility(ogpPreviewTabContainer, true);
            }
        } catch (_error) {
            // ignore
        }
    }

    public handleTextChange(): void {
        WysiwygUtils.syncTextarea(this.quill, this.textarea);

        const content = this.quill.root.innerHTML;
        const extracted = WysiwygUtils.extractAndStoreSNSLinks(content, this.textarea);

        if (!this.isProcessingEmbedCleanup && extracted && typeof extracted.cleanedContent === "string") {
            const cleaned = extracted.cleanedContent;
            if (cleaned && cleaned !== content) {
                this.isProcessingEmbedCleanup = true;

                try {
                    const selection = this.quill.getSelection();
                    const savedIndex = selection ? selection.index : this.quill.getLength();

                    const delta = this.quill.clipboard.convert(cleaned);
                    this.quill.setContents(delta, "api");

                    setTimeout(() => {
                        try {
                            const newLength = this.quill.getLength();
                            const restoredIndex = Math.min(savedIndex, newLength - 1);
                            this.quill.setSelection(restoredIndex, 0, "silent");
                        } catch (_error) {
                            // ignore
                        }
                        this.isProcessingEmbedCleanup = false;
                    }, 0);
                } catch (_error) {
                    try {
                        this.quill.root.innerHTML = cleaned;
                    } catch (__error) {
                        // ignore
                    }
                    this.isProcessingEmbedCleanup = false;
                }

                return;
            }
        }

        if (!this.isProcessingAutoLink) {
            if (this.autoLinkTimeout) {
                clearTimeout(this.autoLinkTimeout);
            }

            this.autoLinkTimeout = setTimeout(() => {
                this.isProcessingAutoLink = true;
                this.convertOgpUrlsToLinks(this.quill.root.innerHTML);
                this.isProcessingAutoLink = false;
            }, 100);
        }

        if (!this.isProcessingAutoLink) {
            const ogpPreviewContainer = this.getOgpPreviewContainer();
            const ogpPreviewTabContainer = this.getOgpPreviewTabContainer();
            if (ogpPreviewContainer || ogpPreviewTabContainer) {
                const ogpPreview = new OgpPreviewHandler() as OgpPreviewHandlerLike;
                const urls = this.detectOgpUrls(content);

                const urlsChanged = JSON.stringify(urls.sort()) !== JSON.stringify(this.lastOgpUrls.sort());

                if (urlsChanged) {
                    this.lastOgpUrls = [...urls];

                    if (urls.length === 0) {
                        if (ogpPreviewContainer) {
                            ogpPreviewContainer.innerHTML = "";
                            ogpPreview.setPreviewSectionVisibility(ogpPreviewContainer, false);
                        }

                        if (ogpPreviewTabContainer) {
                            ogpPreviewTabContainer.innerHTML = "";
                            ogpPreview.setPreviewSectionVisibility(ogpPreviewTabContainer, false);
                        }
                    } else {
                        if (ogpPreviewContainer) {
                            void ogpPreview.updatePreviewWithUrls(urls, ogpPreviewContainer);
                            ogpPreview.setPreviewSectionVisibility(ogpPreviewContainer, true);
                        }

                        if (ogpPreviewTabContainer) {
                            void ogpPreview.updatePreviewWithUrls(urls, ogpPreviewTabContainer);
                            ogpPreview.setPreviewSectionVisibility(ogpPreviewTabContainer, true);
                        }
                    }
                }
            }
        }

        const text = this.quill.getText().trim();
        const editor = this.quill.root;

        if (text.length === 0) {
            editor.classList.add("ql-blank");
        } else {
            editor.classList.remove("ql-blank");
        }
    }

    public handleInitialRender(): void {
        this.updateOgpPreviewNow();
    }

    public handleFormSubmit(): void {
        WysiwygUtils.syncTextarea(this.quill, this.textarea);
        const content = this.quill.root.innerHTML;
        WysiwygUtils.extractAndStoreSNSLinks(content, this.textarea);
    }

    public handleToolbarImage(): void {
        this.handleImageUpload();
    }

    public handleToolbarVideo(): void {
        void this.handleVideoUpload();
    }

    public handlePasteCapture(event: ClipboardEvent): void {
        this.handlePaste(event);
    }

    public handleDropCapture(event: DragEvent): void {
        this.handleDrop(event);
    }

    public handleContentImageListClick(event: MouseEvent): void {
        const deleteButton = WysiwygForm.getContentImageDeleteButton(event);
        if (!(deleteButton instanceof HTMLElement)) {
            return;
        }

        const filename = String(deleteButton.getAttribute("data-filename") || "").trim();
        if (!filename) {
            return;
        }

        void this.handleDelete(filename);
    }

    public convertOgpUrlsToLinks(content: string): void {
        const urlInParagraphPattern = Patterns.reset(Patterns.URL_IN_PARAGRAPH);
        const snsPlatforms = Patterns.OGP_EXCLUDED_SNS_PLATFORMS;

        let modified = false;
        const newContent = content.replace(urlInParagraphPattern, (match, spaceBefore, url, spaceAfter) => {
            const cleanedUrl = UrlExtractor.cleanUrl(url);
            const isSNS = snsPlatforms.some((pattern) => pattern.test(cleanedUrl));

            if (isSNS) {
                return match;
            }

            modified = true;
            return `<p>${spaceBefore}<a href="${cleanedUrl}" target="_blank" rel="noopener noreferrer">${cleanedUrl}</a>${spaceAfter}</p>`;
        });

        if (modified && newContent !== content) {
            try {
                const selection = this.quill.getSelection();
                const savedIndex = selection ? selection.index : this.quill.getLength();

                const delta = this.quill.clipboard.convert(newContent);
                this.quill.setContents(delta, "api");

                setTimeout(() => {
                    try {
                        const newLength = this.quill.getLength();
                        const restoredIndex = Math.min(savedIndex, newLength - 1);
                        this.quill.setSelection(restoredIndex, 0, "silent");
                    } catch (_error) {
                        // Ignore range errors during restoration
                    }
                }, 0);
            } catch (_error) {
                this.quill.root.innerHTML = newContent;
            }
        }
    }

    public handleImageUpload(): void {
        WysiwygForm.openImagePickerMultiple(async (files) => {
            await this.handleImageFiles(files);
        });
    }

    public async handleVideoUpload(): Promise<void> {
        WysiwygForm.openVideoPickerMultiple(async (files) => {
            await this.handleVideoFiles(files);
        });
    }

    public async handleDelete(filename: string): Promise<void> {
        const boardId = this.textarea.dataset.boardId || "default";

        const confirmTemplate = translator?.getTranslation("wysiwyg_confirm_delete") || "Delete image {filename}?";
        const confirmMessage = confirmTemplate.replace("{filename}", filename);
        const deleteFailedMessage = translator?.getTranslation("wysiwyg_media_delete_failed") || "Media delete failed";

        const confirmed = await notifier.confirm(confirmMessage);

        if (!confirmed) {
            return;
        }

        try {
            const result = (await WysiwygAPI.deleteMedia(filename, boardId)) as DeleteMediaResult;

            if (result.success === 1) {
                this.render.removeContentImage(filename);
                this.render.updateContentImagesList();

                const imgElements = WysiwygForm.getImagesByFilename(this.quill.root, filename);
                imgElements.forEach((img) => {
                    const quillGlobal = (
                        window as Window & { Quill?: { find(node: Element): { remove(): void } | null } }
                    ).Quill;
                    const blot = quillGlobal?.find ? quillGlobal.find(img) : null;
                    if (blot) {
                        blot.remove();
                    }
                });

                WysiwygUtils.syncTextarea(this.quill, this.textarea);

                const message = translator?.getTranslation("wysiwyg_image_deleted") || "Image deleted successfully";
                notifier.success(message);
            } else {
                const errorMessage =
                    typeof result.message === "string" && result.message ? result.message : deleteFailedMessage;
                notifier.error(errorMessage);
                throw new Error(errorMessage);
            }
        } catch (error) {
            console.error("Delete error:", error);
            const errorMessage = (() => {
                if (!(error instanceof Error) || !error.message) {
                    return deleteFailedMessage;
                }

                if (error.message === "Media delete failed" || /^Delete failed: HTTP \d+/u.test(error.message)) {
                    return deleteFailedMessage;
                }

                return error.message;
            })();

            notifier.error(errorMessage);
        }
    }

    public handlePaste(event: ClipboardEvent): void {
        try {
            const clipboard = event.clipboardData;
            const textPlain = clipboard?.getData("text/plain") || "";
            const textHtml = clipboard?.getData("text/html") || "";

            const raw = textPlain || textHtml;
            if (raw) {
                const detected = WysiwygUtils.detectSnsUrlsFromText(raw);
                if (detected.length > 0) {
                    const lines = String(textPlain || raw)
                        .replace(/\r\n/g, "\n")
                        .replace(/\r/g, "\n")
                        .split("\n");

                    const normalizeLine = (line: string): string =>
                        String(line || "")
                            .trim()
                            .replace(/&amp;/g, "&");

                    const lineSet = new Set(lines.map(normalizeLine).filter(Boolean));
                    const standaloneUrls = detected.filter((url) => lineSet.has(String(url).trim()));

                    const rawStr = String(raw);
                    const hasIframeLike = rawStr.includes("<iframe") || rawStr.includes("&lt;iframe");
                    const urlsToAdd = hasIframeLike ? detected : standaloneUrls;

                    if (urlsToAdd.length > 0) {
                        WysiwygUtils.mergeSnsLinksIntoEditableList(this.textarea, urlsToAdd);

                        const isFacebookEmbed =
                            hasIframeLike &&
                            (rawStr.includes("facebook.com/plugins/") || rawStr.includes("plugins/post.php"));
                        const isInstagramEmbed =
                            rawStr.includes("instagram-media") || rawStr.includes("data-instgrm-permalink");
                        const isTikTokEmbed = rawStr.includes("tiktok-embed") || rawStr.includes("tiktok.com/embed.js");

                        if (isFacebookEmbed || isInstagramEmbed || isTikTokEmbed) {
                            event.preventDefault();
                            event.stopPropagation();
                            return;
                        }
                    }
                }
            }
        } catch (error) {
            console.warn("WysiwygHandler.handlePaste: SNS detect failed", error);
        }

        WysiwygHandler.handlePasteEvent(
            event,
            (files) => this.handleImageFiles(files),
            (files) => this.handleVideoFiles(files),
        );
    }

    public handleDrop(event: DragEvent): void {
        if (event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.length) {
            event.preventDefault();
            event.stopPropagation();
        }
    }

    public async handleImageFiles(files: FileList): Promise<void> {
        const boardId = this.textarea.dataset.boardId || "default";
        await WysiwygUtils.processImageFiles(files, async (file, type) => {
            await WysiwygUtils.uploadMedia(
                file,
                type,
                boardId,
                this.quill,
                this.render,
                (filename: string) => this.handleDelete(filename),
                this.textarea,
                WysiwygAPI,
            );
        });
    }

    public async handleVideoFiles(files: FileList): Promise<void> {
        const boardId = this.textarea.dataset.boardId || "default";
        await WysiwygUtils.processVideoFiles(files, async (file, type) => {
            await WysiwygUtils.uploadMedia(
                file,
                type,
                boardId,
                this.quill,
                this.render,
                (filename: string) => this.handleDelete(filename),
                this.textarea,
                WysiwygAPI,
            );
        });
    }
}

export { WysiwygHandler };
